import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchAnatelConselho } from '@/services/anatel';
import type { ConselhoBench, ConselhoMethod, ConselhoRun } from '@/services/types';

/**
 * The plain-language methodology for the ANATEL Conselho Diretor bench.
 *
 * Didactic copy (i18n) over live lines read from the SAME endpoint and query
 * key as /anatel/conselho: the registered runs and their diagnostics, the
 * balance tests, the stability reading, the floors and the era rule. The copy
 * carries no numbers of its own.
 */
export function AnatelConselhoMethodologyPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['anatel', 'conselho'],
    queryFn: fetchAnatelConselho,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const twoDp = new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 });

  const k = (key: string) => `anatel:conselhoMethodology.sections.${key}`;
  const p = (key: string) => <p className="text-sm text-muted-foreground">{t(k(key))}</p>;
  const line = (text: string) => <p className="text-sm text-foreground">{text}</p>;
  // A live line waits on the bench; the prose around it never does.
  const live = (name: string, render: (bench: ConselhoBench) => ReactNode) => {
    if (isPending) return <SkeletonLoader rows={1} />;
    if (error || !data) return null;
    return <div data-live={name}>{render(data)}</div>;
  };
  const withMethod = (name: string, render: (method: ConselhoMethod) => ReactNode) =>
    live(name, (bench) => (bench.method ? render(bench.method) : null));

  const section = (key: string, children: ReactNode) => (
    <section className="space-y-2 rounded-lg border bg-card p-4" data-section={key}>
      <h2 className="text-sm font-semibold text-foreground">{t(k(`${key}.title`))}</h2>
      {children}
    </section>
  );

  const runs = (bench: ConselhoBench): ConselhoRun[] =>
    [bench.severity_run, bench.follow_run, bench.position_run, bench.propensity_run].filter(
      (r): r is ConselhoRun => r != null,
    );
  const diag = (v: number | null) => (v === null ? '—' : twoDp.format(v));

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:conselhoMethodology.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('anatel:conselhoMethodology.subtitle')}</p>
      </header>

      {error ? <ErrorMessage message={error.message} onRetry={() => void refetch()} /> : null}
      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {section('sources', <>{p('sources.p1')}{p('sources.p2')}</>)}
      {section(
        'runs',
        <>
          {p('runs.p1')}
          {p('runs.p2')}
          {live('runs', (bench) => {
            const rows = runs(bench);
            if (!rows.length) return line(t(k('runs.none')));
            return (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="methodology-runs">
                  <thead className="bg-muted text-left">
                    <tr>
                      <th className="p-2">{t(k('runs.model'))}</th>
                      <th className="p-2 text-right">{t(k('runs.obs'))}</th>
                      <th className="p-2">{t(k('runs.accepted'))}</th>
                      <th className="p-2 text-right">{t(k('runs.rhat'))}</th>
                      <th className="p-2 text-right">{t(k('runs.ess'))}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.run_id} className="border-t">
                        <td className="p-2">
                          {r.model} <span className="text-muted-foreground">{r.model_version}</span>
                        </td>
                        <td className="p-2 text-right tabular-nums">{number.format(r.n_obs)}</td>
                        <td className="p-2">{t(k(r.accepted ? 'runs.yes' : 'runs.no'))}</td>
                        <td className="p-2 text-right tabular-nums">{diag(r.max_rhat)}</td>
                        <td className="p-2 text-right tabular-nums">
                          {r.min_ess === null ? '—' : number.format(Math.round(r.min_ess))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </>,
      )}
      {section(
        'severity',
        <>
          {p('severity.p1')}
          {withMethod('subject', (m) => line(t(k('severity.p2'), { subject: m.floors.severity_subject })))}
        </>,
      )}
      {section('follow', <>{p('follow.p1')}{p('follow.p2')}</>)}
      {section(
        'positions',
        <>
          {p('positions.p1')}
          {withMethod('floors', (m) =>
            line(t(k('positions.p2'), { metric: m.floors.metric, position: m.floors.position })),
          )}
        </>,
      )}
      {section(
        'evidence',
        <>
          {p('evidence.p1')}
          {p('evidence.p2')}
          {live('evidence', (bench) =>
            line(
              t(k('evidence.live'), {
                significant: bench.evidence.significant_tests,
                tests: bench.evidence.tests,
                bonferroni: bench.evidence.bonferroni_significant ?? 0,
              }),
            ),
          )}
        </>,
      )}
      {section(
        'reading',
        <>
          {p('reading.p1')}
          {p('reading.p2')}
          {live('reading', (bench) => {
            const stable = bench.person_reading?.stable;
            const state = stable == null ? 'unknown' : stable ? 'stable' : 'unstable';
            return line(t(k('reading.live'), { state: t(k(`reading.${state}`)) }));
          })}
        </>,
      )}
      {section(
        'ordering',
        <>
          {p('ordering.p1')}
          {withMethod('era', (m) =>
            m.era_rho_limit != null && m.era_min_members != null
              ? line(t(k('ordering.p2'), { rho: twoDp.format(m.era_rho_limit), members: m.era_min_members }))
              : null,
          )}
          {withMethod('intervals', (m) =>
            line(t('common:conselhoMethodology.intervals', { level: percent.format(m.interval_level) })),
          )}
        </>,
      )}
      {section(
        'pairs',
        withMethod('pairs', (m) =>
          line(t(k('pairs.p1'), { pair: m.floors.pair, contested: m.floors.pair_contested })),
        ),
      )}

      <section className="space-y-2 rounded-lg border bg-card p-4" data-section="never">
        <h2 className="text-sm font-semibold text-foreground">{t('common:conselhoMethodology.neverClaims.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('common:conselhoMethodology.neverClaims.p1')}</p>
        <p className="text-sm text-muted-foreground">{t('common:conselhoMethodology.neverClaims.p2')}</p>
        <p className="text-sm text-muted-foreground">{t('common:conselhoMethodology.neverClaims.p3')}</p>
      </section>

      <nav className="flex flex-wrap gap-4 text-xs">
        <Link to="/anatel/conselho" className="text-primary underline-offset-2 hover:underline">
          {t('anatel:conselhoMethodology.links.bench')}
        </Link>
        <Link to="/anatel/conselho/items" className="text-primary underline-offset-2 hover:underline">
          {t('anatel:conselhoMethodology.links.items')}
        </Link>
      </nav>
    </div>
  );
}
